import React, { useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { useForm, useFieldArray } from 'react-hook-form';
import { Plus, Trash2 } from 'lucide-react';
import { QuizQuestion, QuestionType } from '@/types/quiz';

interface QuestionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Omit<QuizQuestion, 'id' | 'created_at' | 'updated_at'>, options: {
    option_text: string;
    is_correct: boolean;
    explanation?: string;
  }[]) => void;
  question?: QuizQuestion | null;
}

interface QuestionFormData {
  question_text: string;
  question_type: QuestionType;
  points_value: number;
  explanation?: string;
  options: {
    option_text: string;
    is_correct: boolean;
    explanation?: string;
  }[];
}

export const QuestionDialog = ({ isOpen, onClose, onSubmit, question }: QuestionDialogProps) => {
  const { register, handleSubmit, formState: { errors }, setValue, watch, reset, control } = useForm<QuestionFormData>({
    defaultValues: {
      question_text: question?.question_text || '',
      question_type: question?.question_type || 'multiple_choice',
      points_value: question?.points_value || 10,
      explanation: question?.explanation || '',
      options: question?.options?.map(opt => ({
        option_text: opt.option_text,
        is_correct: opt.is_correct,
        explanation: opt.explanation || ''
      })) || [
        { option_text: '', is_correct: true, explanation: '' },
        { option_text: '', is_correct: false, explanation: '' },
        { option_text: '', is_correct: false, explanation: '' },
        { option_text: '', is_correct: false, explanation: '' }
      ]
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'options'
  });

  const questionType = watch('question_type');

  // Initialize options based on question type
  React.useEffect(() => {
    if (questionType === 'true_false') {
      // Reset to true/false options
      reset({
        ...watch(),
        options: [
          { option_text: 'True', is_correct: true, explanation: '' },
          { option_text: 'False', is_correct: false, explanation: '' }
        ]
      });
    } else if (questionType === 'fill_in_blank') {
      // Reset to single correct answer option
      reset({
        ...watch(),
        options: [
          { option_text: '', is_correct: true, explanation: '' }
        ]
      });
    } else if (questionType === 'multiple_choice' && fields.length === 0) {
      // Reset to default multiple choice options if empty
      reset({
        ...watch(),
        options: [
          { option_text: '', is_correct: true, explanation: '' },
          { option_text: '', is_correct: false, explanation: '' },
          { option_text: '', is_correct: false, explanation: '' },
          { option_text: '', is_correct: false, explanation: '' }
        ]
      });
    }
  }, [questionType, reset, watch, fields.length]);

  React.useEffect(() => {
    if (question) {
      reset({
        question_text: question.question_text,
        question_type: question.question_type,
        points_value: question.points_value,
        explanation: question.explanation || '',
        options: question.options?.map(opt => ({
          option_text: opt.option_text,
          is_correct: opt.is_correct,
          explanation: opt.explanation || ''
        })) || []
      });
    } else {
      reset({
        question_text: '',
        question_type: 'multiple_choice',
        points_value: 10,
        explanation: '',
        options: [
          { option_text: '', is_correct: true, explanation: '' },
          { option_text: '', is_correct: false, explanation: '' },
          { option_text: '', is_correct: false, explanation: '' },
          { option_text: '', is_correct: false, explanation: '' }
        ]
      });
    }
  }, [question, reset]);

  const onFormSubmit = (data: QuestionFormData) => {
    onSubmit({
      quiz_id: question?.quiz_id || '', // Use existing quiz_id for updates, empty for new questions
      order_index: question?.order_index || 0, // Use existing order_index for updates
      question_text: data.question_text,
      question_type: data.question_type,
      points_value: data.points_value,
      explanation: data.explanation,
    }, data.options);
  };

  const addOption = () => {
    append({ option_text: '', is_correct: false, explanation: '' });
  };

  const removeOption = (index: number) => {
    if (fields.length > 2) {
      remove(index);
    }
  };

  const setCorrectAnswer = (index: number) => {
    fields.forEach((_, i) => {
      setValue(`options.${i}.is_correct`, i === index);
    });
  };

  const renderOptions = () => {
    if (questionType === 'true_false') {
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Card className="p-4">
              <div className="flex items-center space-x-2">
                <Switch
                  checked={watch('options.0.is_correct')}
                  onCheckedChange={(checked) => {
                    setValue('options.0.option_text', 'True');
                    setValue('options.0.is_correct', checked);
                    setValue('options.1.option_text', 'False');
                    setValue('options.1.is_correct', !checked);
                  }}
                />
                <Label>True is correct</Label>
              </div>
            </Card>
            <Card className="p-4">
              <div className="flex items-center space-x-2">
                <Switch
                  checked={watch('options.1.is_correct')}
                  onCheckedChange={(checked) => {
                    setValue('options.0.option_text', 'True');
                    setValue('options.0.is_correct', !checked);
                    setValue('options.1.option_text', 'False');
                    setValue('options.1.is_correct', checked);
                  }}
                />
                <Label>False is correct</Label>
              </div>
            </Card>
          </div>
        </div>
      );
    }

    if (questionType === 'fill_in_blank') {
      return (
        <div className="space-y-4">
          <div>
            <Label>Correct Answer</Label>
            <Input
              {...register('options.0.option_text', { required: 'Correct answer is required' })}
              placeholder="Enter the correct answer"
              onChange={(e) => {
                setValue('options.0.option_text', e.target.value);
                setValue('options.0.is_correct', true);
              }}
            />
          </div>
        </div>
      );
    }

    // Multiple choice options
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label>Answer Options</Label>
          <Button type="button" size="sm" onClick={addOption}>
            <Plus className="h-4 w-4 mr-1" />
            Add Option
          </Button>
        </div>
        
        {fields.map((field, index) => (
          <Card key={field.id} className="p-4">
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Switch
                  checked={watch(`options.${index}.is_correct`)}
                  onCheckedChange={() => setCorrectAnswer(index)}
                />
                <Label className="text-xs text-green-600">Correct Answer</Label>
                <div className="flex-1" />
                {fields.length > 2 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => removeOption(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
              
              <div>
                <Input
                  {...register(`options.${index}.option_text`, { required: 'Option text is required' })}
                  placeholder={`Option ${index + 1}`}
                />
              </div>
              
              <div>
                <Input
                  {...register(`options.${index}.explanation`)}
                  placeholder="Explanation (optional)"
                />
              </div>
            </div>
          </Card>
        ))}
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {question ? 'Edit Question' : 'Add Question'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
          <div className="space-y-4">
            <div>
              <Label htmlFor="question_text">Question</Label>
              <Textarea
                id="question_text"
                {...register('question_text', { required: 'Question is required' })}
                placeholder="Enter your question"
                rows={3}
              />
              {errors.question_text && (
                <p className="text-sm text-red-600 mt-1">{errors.question_text.message}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="question_type">Question Type</Label>
                <Select
                  value={questionType}
                  onValueChange={(value: QuestionType) => setValue('question_type', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select question type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="multiple_choice">Multiple Choice</SelectItem>
                    <SelectItem value="true_false">True/False</SelectItem>
                    <SelectItem value="fill_in_blank">Fill in the Blank</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="points_value">Points Value</Label>
                <Input
                  id="points_value"
                  type="number"
                  {...register('points_value', { 
                    required: 'Points value is required',
                    min: { value: 1, message: 'Points must be at least 1' }
                  })}
                  placeholder="10"
                />
                {errors.points_value && (
                  <p className="text-sm text-red-600 mt-1">{errors.points_value.message}</p>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="explanation">Explanation (Optional)</Label>
              <Textarea
                id="explanation"
                {...register('explanation')}
                placeholder="Provide an explanation for this question"
                rows={2}
              />
            </div>

            {renderOptions()}
          </div>

          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">
              {question ? 'Update Question' : 'Save Question'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};